import { ref, computed, type ComputedRef } from 'vue';
import type {
  Entitlement,
  EntitlementData,
  GenerateEntitlementResponse,
  ValidateEntitlementResponse,
  SaveEntitlementResponse,
  GetCurrentEntitlementResponse,
  ExtendEntitlementResponse,
} from '@maso-dj/shared-types';
import { apiClient, authenticatedApiClient } from '../services/ApiClient';
import { useAuth } from './useAuth';

// Time constants
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

const currentEntitlement = ref<Entitlement | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const effectiveExpiresAtValue = ref<string | null>(null);
const graceCountValue = ref<number>(0);

interface UseEntitlementReturn {
  // State
  currentEntitlement: ComputedRef<Entitlement | null>;
  isActive: ComputedRef<boolean>;
  email: ComputedRef<string | null>;
  expiresAt: ComputedRef<string | null>;
  planType: ComputedRef<string | null>;
  expiryDate: ComputedRef<Date | null>;
  daysUntilExpiry: ComputedRef<number | null>;
  isExpiringSoon: ComputedRef<boolean>;
  effectiveExpiresAt: ComputedRef<string | null>;
  effectiveExpiryDate: ComputedRef<Date | null>;
  effectiveDaysUntilExpiry: ComputedRef<number | null>;
  graceCount: ComputedRef<number>;
  usingGracePeriod: ComputedRef<boolean>;
  loading: ComputedRef<boolean>;
  error: ComputedRef<string | null>;

  // Methods
  generateEntitlement: () => Promise<string | null>;
  validateEntitlement: (token: string) => Promise<EntitlementData | null>;
  saveEntitlement: (token: string) => Promise<boolean>;
  loadEntitlement: () => Promise<void>;
  removeEntitlement: () => Promise<boolean>;
  requestExtension: () => Promise<{ success: boolean; graceCount?: number; error?: string }>;
  downloadEntitlementFile: (token: string, email: string, planType: string, expiresAt: string) => void;
}

/**
 * Composable for managing entitlements (offline license tokens)
 */
export function useEntitlement(): UseEntitlementReturn {
  // Setup authenticated API client with token provider
  const auth = useAuth();
  authenticatedApiClient.setTokenProvider(auth.getToken);

  const isActive = computed(() => {
    return currentEntitlement.value?.isActive ?? false;
  });

  const email = computed(() => {
    return currentEntitlement.value?.data?.email ?? null;
  });

  const expiresAt = computed(() => {
    return currentEntitlement.value?.data?.expiresAt ?? null;
  });

  const planType = computed(() => {
    return currentEntitlement.value?.data?.planType ?? null;
  });

  const expiryDate = computed(() => {
    if (!expiresAt.value) return null;
    return new Date(expiresAt.value);
  });

  const daysUntilExpiry = computed(() => {
    if (!expiryDate.value) return null;
    const days = Math.floor((expiryDate.value.getTime() - Date.now()) / MILLISECONDS_PER_DAY);
    return days;
  });

  const isExpiringSoon = computed(() => {
    const days = daysUntilExpiry.value;
    return days !== null && days >= 0 && days <= 14;
  });

  const effectiveExpiresAt = computed(() => effectiveExpiresAtValue.value);

  const effectiveExpiryDate = computed(() => {
    if (!effectiveExpiresAtValue.value) return null;
    return new Date(effectiveExpiresAtValue.value);
  });

  const effectiveDaysUntilExpiry = computed(() => {
    if (!effectiveExpiryDate.value) return null;
    return Math.floor((effectiveExpiryDate.value.getTime() - Date.now()) / MILLISECONDS_PER_DAY);
  });

  const graceCount = computed(() => graceCountValue.value);

  const usingGracePeriod = computed(() => graceCountValue.value > 0);

  /**
   * Generate an entitlement token (network mode only)
   * Requires active subscription
   */
  async function generateEntitlement(): Promise<string | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await authenticatedApiClient.get<GenerateEntitlementResponse>(
        '/api/entitlements/generate',
        { description: 'Generate entitlement' }
      );

      if (response.success) {
        return response.token;
      }

      error.value = response.error || 'Failed to generate entitlement';
      return null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Validate an entitlement token
   * Returns the entitlement data if valid
   */
  async function validateEntitlement(token: string): Promise<EntitlementData | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.post<ValidateEntitlementResponse>(
        '/api/entitlements/validate',
        { token },
        { description: 'Validate entitlement' }
      );

      if (response.success) {
        return response.data;
      }

      error.value = response.error || 'Invalid entitlement token';
      return null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Save entitlement locally (local-only mode)
   */
  async function saveEntitlement(token: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.post<SaveEntitlementResponse>(
        '/api/entitlements/save',
        { token },
        { description: 'Save entitlement' }
      );

      if (response.success) {
        currentEntitlement.value = {
          token,
          data: response.data,
          isActive: response.isActive,
        };
        return true;
      }

      error.value = response.error || 'Failed to save entitlement';
      return false;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load current entitlement (local-only mode)
   */
  async function loadEntitlement(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.get<GetCurrentEntitlementResponse>(
        '/api/entitlements/current',
        { description: 'Load entitlement' }
      );

      if (response.success && response.data) {
        currentEntitlement.value = {
          token: '',
          data: response.data,
          isActive: response.isActive,
        };
        effectiveExpiresAtValue.value = response.effectiveExpiresAt;
        graceCountValue.value = response.graceCount;
      } else {
        currentEntitlement.value = null;
        effectiveExpiresAtValue.value = null;
        graceCountValue.value = 0;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      currentEntitlement.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Remove current entitlement (local-only mode)
   */
  async function removeEntitlement(): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.delete(
        '/api/entitlements/current',
        { description: 'Remove entitlement' }
      );

      if (response.success) {
        currentEntitlement.value = null;
        return true;
      }

      error.value = response.error || 'Failed to remove entitlement';
      return false;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Request a grace period extension (local-only mode)
   * Grants 7 additional days, up to 3 times total
   */
  async function requestExtension(): Promise<{ success: boolean; graceCount?: number; error?: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.post<ExtendEntitlementResponse>(
        '/api/entitlements/extend',
        {},
        { description: 'Request grace extension' }
      );

      if (response.success && response.effectiveExpiresAt != null && response.graceCount != null) {
        effectiveExpiresAtValue.value = response.effectiveExpiresAt;
        graceCountValue.value = response.graceCount;

        // Update isActive on the entitlement
        if (currentEntitlement.value) {
          currentEntitlement.value = {
            ...currentEntitlement.value,
            isActive: new Date(response.effectiveExpiresAt).getTime() >= Date.now(),
          };
        }

        return { success: true, graceCount: response.graceCount };
      }

      const errMsg = response.error || 'Failed to extend entitlement';
      error.value = errMsg;
      return { success: false, error: errMsg };
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error occurred';
      error.value = errMsg;
      return { success: false, error: errMsg };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Download entitlement as file
   */
  function downloadEntitlementFile(token: string, email: string, planType: string, expiresAt: string): void {
    const expiresAtDate = new Date(expiresAt).toLocaleDateString();
    const content = `=== MasoDJ Entitlement ===

Licensed to: ${email}
Plan: ${planType}
Expires: ${expiresAtDate}

Entitlement Token:
${token}

=== Do not share this file ===`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `masodj-entitlement-${email.replace('@', '_at_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return {
    // State
    currentEntitlement: computed(() => currentEntitlement.value),
    isActive,
    email,
    expiresAt,
    planType,
    expiryDate,
    daysUntilExpiry,
    isExpiringSoon,
    effectiveExpiresAt,
    effectiveExpiryDate,
    effectiveDaysUntilExpiry,
    graceCount,
    usingGracePeriod,
    loading: computed(() => loading.value),
    error: computed(() => error.value),

    // Methods
    generateEntitlement,
    validateEntitlement,
    saveEntitlement,
    loadEntitlement,
    removeEntitlement,
    requestExtension,
    downloadEntitlementFile,
  };
}
